<template>
  <div class="shop-config">
    <h2>ตั้งค่าการจอง</h2>

    <!-- การตั้งค่าวันที่เปิดจอง -->
    <div class="config-section">
      <h3>ตั้งค่าวันที่เปิดจอง</h3>
      <div class="date-settings">
        <div class="form-group">
          <label>วันที่เปิดให้จอง:</label>
          <div class="date-inputs">
            <label>
              <input type="checkbox" v-model="bookingDays.monday" /> จันทร์
            </label>
            <label>
              <input type="checkbox" v-model="bookingDays.tuesday" /> อังคาร
            </label>
            <label>
              <input type="checkbox" v-model="bookingDays.wednesday" /> พุธ
            </label>
            <label>
              <input type="checkbox" v-model="bookingDays.thursday" /> พฤหัสบดี
            </label>
            <label>
              <input type="checkbox" v-model="bookingDays.friday" /> ศุกร์
            </label>
            <label>
              <input type="checkbox" v-model="bookingDays.saturday" /> เสาร์
            </label>
            <label>
              <input type="checkbox" v-model="bookingDays.sunday" /> อาทิตย์
            </label>
          </div>
        </div>
        <div class="form-group">
          <label>เวลาที่เปิดให้จอง:</label>
          <div class="time-inputs">
            <input type="time" v-model="openTime" /> ถึง
            <input type="time" v-model="closeTime" />
          </div>
        </div>
      </div>
    </div>

    <!-- การจัดการโต๊ะ/ห้อง -->
    <div class="config-section">
      <h3>จัดการ{{ shopType === 'hotel' ? 'ห้องพัก' : 'โต๊ะอาหาร' }}</h3>
      <div class="room-management">
        <div class="type-selector">
          <select v-model="selectedType" :disabled="true">
            <option v-for="type in bookingTypes" :key="type.id" :value="type.id" :selected="type.id === shopType">
              {{ type.name }}
            </option>
          </select>
        </div>

        <div class="rooms-grid">
          <div v-for="room in roomsByType[shopType]" :key="room.id" class="room-item">
            <div class="room-header">
              <input type="text" v-model="room.name" :placeholder="shopType === 'hotel' ? 'ชื่อห้อง' : 'ชื่อโต๊ะ'" />
              <button @click="removeRoom(room.id)" class="delete-btn">ลบ</button>
            </div>
            <div class="room-details">
              <input type="number" v-model="room.capacity" :placeholder="shopType === 'hotel' ? 'จำนวนคน/ห้อง' : 'จำนวนคน/โต๊ะ'" />
              <input type="number" v-model="room.price" placeholder="ราคา" />
              <input type="text" v-model="room.description" placeholder="รายละเอียด" class="full-width" />
            </div>
          </div>
          <button @click="addRoom" class="add-btn">เพิ่ม{{ shopType === 'hotel' ? 'ห้องพัก' : 'โต๊ะอาหาร' }}</button>
        </div>
      </div>
    </div>

    <div class="actions">
      <button @click="saveConfig" class="save-btn">บันทึกการตั้งค่า</button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { doc, getDoc, deleteDoc, writeBatch, getDocs, collection } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useRouter } from 'vue-router';
import { auth, db } from "../firebase";

export default {
  name: 'ShopConfig',
  setup() {
    const router = useRouter();
    const bookingDays = ref({
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    });

    const openTime = ref('09:00');
    const closeTime = ref('22:00');
    const bookingTypes = ref([
      { id: 'hotel', name: 'ห้องพัก' },
      { id: 'restaurant', name: 'ร้านอาหาร' }
    ]);
    const selectedType = ref('');
    const roomsByType = ref({
      hotel: [],
      restaurant: []
    });
    const shopId = ref(null);
    const shopType = ref(null);
    const type = ref(null);

    // ตรวจสอบสถานะการเข้าสู่ระบบ
    const checkAuthState = async () => {
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          console.log("Email:", user.email);
          console.log("Display Name:", user.displayName);
          console.log("UID:", user.uid);

          try {
            const userDocRef = doc(db, "users", user.email);
            const userDoc = await getDoc(userDocRef);

            if (userDoc.exists()) {
              console.log(`User ${user.email} exists. Retrieving shop data...`);
              const userData = userDoc.data();
              shopId.value = userData.shopId;

              const shopDocRef = doc(db, "shops", shopId.value);
              const shopDoc = await getDoc(shopDocRef);

              if (shopDoc.exists()) {
                const shopData = shopDoc.data();
                shopType.value = shopData.typeshop;
                type.value = shopType.value;
                selectedType.value = shopType.value;
                console.log("Shop Type:", shopType.value);

                // ตรวจสอบสิทธิ์ admin
                const idTokenResult = await user.getIdTokenResult(true);
                console.log("🛠️ User Role:", idTokenResult.claims.role || "Not Assigned");
                
                if (!idTokenResult.claims.role || 
                    (idTokenResult.claims.role !== 'admin' && idTokenResult.claims.role !== 'super_admin')) {
                  console.log("User is not admin or super_admin. Redirecting...");
                  alert('คุณไม่มีสิทธิ์ในการเข้าถึงการตั้งค่านี้');
                  router.push('/');
                  return;
                }

                await loadConfig();
              } else {
                console.log("Shop not found");
                alert('ไม่พบข้อมูลร้าน');
                router.push('/');
              }
            } else {
              console.log("User document not found");
              alert('ไม่พบข้อมูลผู้ใช้');
              router.push('/');
            }
          } catch (error) {
            console.error("Error checking auth state:", error);
            alert('เกิดข้อผิดพลาดในการตรวจสอบสิทธิ์: ' + error.message);
          }
        } else {
          console.log("User not authenticated. Redirecting to login...");
          router.push('/login');
        }
      });
    };

    // โหลดข้อมูลการตั้งค่า
    const loadConfig = async () => {
      try {
        if (!shopId.value) return;

        // ตรวจสอบสิทธิ์ก่อนโหลดข้อมูล
        const idTokenResult = await auth.currentUser.getIdTokenResult(true);
        const userRole = idTokenResult.claims.role;
        console.log("Loading config for role:", userRole);

        // ถ้าเป็น super_admin ให้โหลดข้อมูลจาก shop1
        if (userRole === 'super_admin') {
          shopId.value = 'shop1';
        }

        // 3. โหลดข้อมูลห้องพักจาก collection hotelRooms
        const hotelRoomsRef = collection(db, "shops", shopId.value, "hotelRooms");
        const hotelRoomsSnapshot = await getDocs(hotelRoomsRef);
        
        if (!hotelRoomsSnapshot.empty) {
          roomsByType.value.hotel = hotelRoomsSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name || '',
            capacity: doc.data().capacity || 1,
            price: doc.data().price || 0,
            description: doc.data().description || ''
          }));
        } else {
          console.log("No hotel rooms found");
          roomsByType.value.hotel = [];
        }

        // 4. โหลดข้อมูลโต๊ะจาก collection restaurantTables
        const restaurantTablesRef = collection(db, "shops", shopId.value, "restaurantTables");
        const restaurantTablesSnapshot = await getDocs(restaurantTablesRef);
        
        if (!restaurantTablesSnapshot.empty) {
          roomsByType.value.restaurant = restaurantTablesSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name || '',
            capacity: doc.data().capacity || 1,
            price: doc.data().price || 0,
            description: doc.data().description || ''
          }));
        } else {
          console.log("No restaurant tables found");
          roomsByType.value.restaurant = [];
        }

        // 5. โหลดการตั้งค่าทั่วไป
        const configRef = doc(db, "shop_config", shopId.value);
        const configDoc = await getDoc(configRef);

        if (configDoc.exists()) {
          const data = configDoc.data();
          bookingDays.value = data.bookingDays || {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false
          };
          openTime.value = data.openTime || '09:00';
          closeTime.value = data.closeTime || '22:00';
        } else {
          console.log("No config found, using defaults");
        }

        console.log("Config loaded successfully for shop:", shopId.value);

      } catch (error) {
        console.error('Error loading config:', error);
        if (error.code === 'permission-denied') {
          console.error('Permission denied for shop:', shopId.value);
          if (auth.currentUser) {
            const idTokenResult = await auth.currentUser.getIdTokenResult(true);
            console.error('Current role:', idTokenResult.claims.role);
          }
          alert('คุณไม่มีสิทธิ์ในการเข้าถึงข้อมูลนี้');
        } else {
          alert(error.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
        }
      }
    };

    // เพิ่มห้อง/โต๊ะ
    const addRoom = async () => {
      try {
        if (!shopType.value) return;

        const prefix = shopType.value === 'hotel' ? 'room' : 'table';
        const currentCount = roomsByType.value[shopType.value].length + 1;
        
        const newItem = {
          id: `${prefix}${currentCount}`,
          name: `${shopType.value === 'hotel' ? 'Room' : 'Table'} ${currentCount}`,
          capacity: shopType.value === 'hotel' ? 2 : 4,
          price: shopType.value === 'hotel' ? 1500 : 800,
          description: shopType.value === 'hotel' ? 
            'A comfortable room with a king-size bed' : 
            'Standard table'
        };

        roomsByType.value[shopType.value].push(newItem);
      } catch (error) {
        console.error('Error adding room:', error);
        alert(error.message || 'เกิดข้อผิดพลาดในการเพิ่มข้อมูล');
      }
    };

    // ลบห้อง/โต๊ะ
    const removeRoom = async (roomId) => {
      try {
        if (!shopType.value) return;

        // ลบจาก Firestore
        const collectionName = shopType.value === 'hotel' ? 'hotelRooms' : 'restaurantTables';
        await deleteDoc(doc(db, `shops/${shopId.value}/${collectionName}/${roomId}`));

        // ลบจาก local state
        const rooms = roomsByType.value[shopType.value];
        const index = rooms.findIndex(room => room.id === roomId);
        if (index !== -1) {
          rooms.splice(index, 1);
        }

        alert('ลบข้อมูลเรียบร้อยแล้ว');
      } catch (error) {
        console.error('Error removing room:', error);
        if (error.code === 'permission-denied') {
          alert('คุณไม่มีสิทธิ์ในการลบข้อมูลนี้');
        } else {
          alert(error.message || 'เกิดข้อผิดพลาดในการลบข้อมูล');
        }
      }
    };

    // บันทึกการตั้งค่า
    const saveConfig = async () => {
      try {
        if (!shopId.value) return;

        const batch = writeBatch(db);
        
        // 1. อัพเดทข้อมูลร้าน
        const shopRef = doc(db, 'shops', shopId.value);
        batch.update(shopRef, {
          hotelRooms: roomsByType.value.hotel.map(room => room.id),
          restaurantTables: roomsByType.value.restaurant.map(table => table.id),
          updatedAt: new Date()
        });

        // 2. บันทึกการตั้งค่าทั่วไป
        const configRef = doc(db, 'shop_config', shopId.value);
        batch.set(configRef, {
          bookingDays: bookingDays.value,
          openTime: openTime.value,
          closeTime: closeTime.value,
          updatedAt: new Date()
        });

        // 3. บันทึกข้อมูลห้องพัก
        roomsByType.value.hotel.forEach(room => {
          const roomRef = doc(db, `shops/${shopId.value}/hotelRooms`, room.id);
          batch.set(roomRef, {
            name: room.name,
            capacity: Number(room.capacity),
            price: Number(room.price),
            description: room.description
          });
        });

        // 4. บันทึกข้อมูลโต๊ะอาหาร
        roomsByType.value.restaurant.forEach(table => {
          const tableRef = doc(db, `shops/${shopId.value}/restaurantTables`, table.id);
          batch.set(tableRef, {
            name: table.name,
            capacity: Number(table.capacity),
            price: Number(table.price),
            description: table.description
          });
        });

        // 5. Commit การเปลี่ยนแปลงทั้งหมด
        await batch.commit();

        alert('บันทึกการตั้งค่าเรียบร้อยแล้ว');
      } catch (error) {
        console.error('Error saving config:', error);
        if (error.code === 'permission-denied') {
          alert('คุณไม่มีสิทธิ์ในการบันทึกข้อมูลนี้');
        } else {
          alert(error.message || 'เกิดข้อผิดพลาดในการบันทึกการตั้งค่า');
        }
      }
    };

    onMounted(() => {
      checkAuthState();
    });

    return {
      bookingDays,
      openTime,
      closeTime,
      bookingTypes,
      selectedType,
      roomsByType,
      shopType,
      type,
      addRoom,
      removeRoom,
      saveConfig
    };
  }
};
</script>

<style scoped>
.shop-config {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.config-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

h2, h3 {
  color: #333;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.date-inputs {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
  margin-top: 10px;
}

.time-inputs {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 10px;
}

.booking-types {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.booking-type {
  display: flex;
  gap: 10px;
  align-items: center;
}

.rooms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.room-item {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 15px;
}

.room-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.room-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.room-details .full-width {
  grid-column: 1 / -1;
}

input[type="text"],
input[type="number"],
input[type="time"],
select {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 100%;
}

.add-btn {
  background: #28a745;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

.delete-btn {
  background: #dc3545;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.save-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  margin-top: 20px;
}

button:hover {
  opacity: 0.9;
}

.actions {
  text-align: center;
  margin-top: 30px;
}

.type-selector {
  margin-bottom: 20px;
}

select {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 200px;
}
</style> 